import React, { useState, useEffect } from "react";
import Card from "components/card";
import { MdEdit, MdDelete, MdSearch, MdError, MdClose } from "react-icons/md";
import axios from "axios";
import { Toaster, toast } from "react-hot-toast";

// Separate Loader component to match ViewWarehouse
const Loader = () => (
  <div className="flex h-[400px] items-center justify-center">
    <div className="relative">
      <div className="h-24 w-24 rounded-full border-t-4 border-b-4 border-brand-500"></div>
      <div className="absolute top-0 left-0 h-24 w-24 animate-spin rounded-full border-t-4 border-b-4 border-gray-200"></div>
      <div className="absolute top-1/2 left-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-500"></div>
      <div className="mt-8 text-center">
        <p className="text-lg font-medium text-gray-600 dark:text-white">Loading</p>
        <p className="text-sm text-gray-400">Please wait...</p>
      </div>
    </div>
  </div>
);

// Add EditUserModal component
const EditUserModal = ({ user, onClose, onUpdate }) => {
  const [formData, setFormData] = useState({
    id: user?.id || "",
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    username: user?.username || "",
    email: user?.email || "",
    phone: user?.phone || "",
    role: user?.role || "",
    unit: user?.unit || ""
  });
  const [isUpdating, setIsUpdating] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsUpdating(true);
    await onUpdate(formData);
    setIsUpdating(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto backdrop-blur-sm bg-gray-900/80">
      <div className="relative w-full max-w-2xl p-4">
        <Card extra={"w-full h-full p-6"}>
          {isUpdating && (
            <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/80 dark:bg-navy-700/80">
              <div className="relative">
                <div className="h-24 w-24 rounded-full border-t-4 border-b-4 border-brand-500"></div>
                <div className="absolute top-0 left-0 h-24 w-24 animate-spin rounded-full border-t-4 border-b-4 border-gray-200"></div>
                <div className="absolute top-1/2 left-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-500"></div>
                <div className="mt-8 text-center">
                  <p className="text-lg font-medium text-gray-600 dark:text-white">Updating</p>
                  <p className="text-sm text-gray-400">Please wait...</p>
                </div>
              </div>
            </div>
          )}

          <div className="mb-8 flex items-center justify-between">
            <div>
              <h4 className="text-2xl font-bold text-navy-700 dark:text-white">
                Edit User
              </h4>
            </div>
            <button
              onClick={onClose}
              className="rounded-full bg-lightPrimary p-2 text-brand-500 hover:bg-gray-100 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-600"
            >
              <MdClose className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="text-sm text-navy-700 dark:text-white">First Name*</label>
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-lg border border-gray-300 p-2 outline-none dark:bg-navy-800 dark:text-white"
                  required
                />
              </div>
              <div>
                <label className="text-sm text-navy-700 dark:text-white">Last Name*</label>
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-lg border border-gray-300 p-2 outline-none dark:bg-navy-800 dark:text-white"
                  required
                />
              </div>
              <div>
                <label className="text-sm text-navy-700 dark:text-white">Username</label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  className="mt-1 w-full rounded-lg border border-gray-300 bg-gray-100 p-2 outline-none dark:bg-navy-900 dark:text-gray-400"
                  disabled
                />
              </div>
              <div>
                <label className="text-sm text-navy-700 dark:text-white">Email*</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-lg border border-gray-300 p-2 outline-none dark:bg-navy-800 dark:text-white"
                  required
                />
              </div>
              <div>
                <label className="text-sm text-navy-700 dark:text-white">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-lg border border-gray-300 p-2 outline-none dark:bg-navy-800 dark:text-white"
                  placeholder="+1234567890"
                />
              </div>
              <div>
                <label className="text-sm text-navy-700 dark:text-white">Role*</label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-lg border border-gray-300 p-2 outline-none dark:bg-navy-800 dark:text-white"
                  required
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              {/* Units dropdown - only show when role is "user" */}
              {formData.role === "user" && (
                <div>
                  <label className="text-sm text-navy-700 dark:text-white">
                    Unit*
                  </label>
                  <select
                    name="unit"
                    value={formData.unit || ""}
                    onChange={handleChange}
                    className="mt-1 w-full rounded-lg border border-gray-300 p-2 outline-none dark:bg-navy-800 dark:text-white"
                    required
                  >
                    {/* <option value="">Select a unit</option> */}
                    <option value="RCN Warehouse">RCN Warehouse</option>
                    <option value="Finish Pro Warehouse">Finish Pro Warehouse</option>
                    <option value="Analyst">Analyst</option>
                    <option value="Manual Peeling">Manual Peeling</option>
                    <option value="Field Analyst">Field Analyst</option>
                    <option value="Factory Analyst">Factory Analyst</option>
                  </select>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={onClose}
                disabled={isUpdating}
                className={`rounded-xl bg-gray-500 px-5 py-3 text-base font-medium text-white transition duration-200 hover:bg-gray-600 ${
                  isUpdating ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isUpdating}
                className={`rounded-xl bg-brand-500 px-5 py-3 text-base font-medium text-white transition duration-200 hover:bg-brand-600 ${
                  isUpdating ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isUpdating ? 'Updating...' : 'Update'}
              </button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedUser, setSelectedUser] = useState(null);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('https://usibras-web-backend.onrender.com/api/v1/users');
      if (response.data.users) {
        setUsers(response.data.users);
      }
    } catch (err) {
      setError('Failed to fetch users');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Filter users based on search
  const filteredUsers = users.filter((user) =>
    Object.values(user).some((value) =>
      String(value).toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  // Calculate pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        // Add your delete API call here
        setUsers(users.filter(user => user.id !== id));
      } catch (error) {
        console.error('Error deleting user:', error);
      }
    }
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
  };

  const handleUpdate = async (updatedUser) => {
    try {
      const response = await axios.put('https://usibras-web-backend.onrender.com/api/admin/edit-user', {
        user_id: updatedUser.id,
        username: updatedUser.username,
        email: updatedUser.email,
        first_name: updatedUser.first_name,
        last_name: updatedUser.last_name,
        phone: updatedUser.phone,
        role: updatedUser.role,
        unit: updatedUser.unit || null
      });

      if (response.data.success) {
        setUsers(users.map(user => 
          user.id === updatedUser.id ? updatedUser : user
        ));
        setSelectedUser(null);
        toast.success('User updated successfully');
      } else {
        toast.error(response.data.message || 'Failed to update user');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error(error.response?.data?.message || 'Failed to update user');
    }
  };

  return (
    <div className="mt-3 h-full">
      <Toaster position="top-right" />
      <Card extra={"w-full h-full p-4"}>
        {loading ? (
          <Loader />
        ) : error ? (
          <div className="flex h-[400px] items-center justify-center">
            <div className="text-center">
              <div className="mb-4 text-5xl text-red-500">
                <MdError className="mx-auto h-16 w-16" />
              </div>
              <p className="text-xl font-semibold text-gray-700 dark:text-white">
                Oops! Something went wrong
              </p>
              <p className="mt-2 text-gray-500 dark:text-gray-400">{error}</p>
              <button
                onClick={fetchUsers}
                className="mt-4 rounded-lg bg-brand-500 px-4 py-2 text-white hover:bg-brand-600"
              >
                Try Again
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex items-center gap-2">
                <h4 className="text-xl font-bold text-navy-700 dark:text-white">
                  User Management
                </h4>
                <span className="rounded-full bg-brand-500 px-2 py-1 text-xs text-white">
                  {filteredUsers.length} users
                </span>
              </div>
              <div className="flex w-full flex-col gap-4 md:w-[380px] md:flex-row">
                <div className="flex w-full items-center rounded-lg border bg-white px-3 py-2 shadow-md dark:bg-navy-700">
                  <MdSearch className="h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="ml-2 w-full bg-transparent text-sm font-medium text-gray-600 outline-none dark:text-white"
                  />
                </div>
              </div>
            </div>

            <div className="mt-8 overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="!border-px !border-gray-400">
                    <th className="cursor-pointer border-b-[1px] border-gray-200 pt-4 pb-2 pr-4 text-start">
                      <p className="text-sm font-bold text-gray-600 dark:text-white">First Name</p>
                    </th>
                    <th className="cursor-pointer border-b-[1px] border-gray-200 pt-4 pb-2 pr-4 text-start">
                      <p className="text-sm font-bold text-gray-600 dark:text-white">Last Name</p>
                    </th>
                    <th className="cursor-pointer border-b-[1px] border-gray-200 pt-4 pb-2 pr-4 text-start">
                      <p className="text-sm font-bold text-gray-600 dark:text-white">Username</p>
                    </th>
                    <th className="cursor-pointer border-b-[1px] border-gray-200 pt-4 pb-2 pr-4 text-start">
                      <p className="text-sm font-bold text-gray-600 dark:text-white">Email</p>
                    </th>
                    <th className="cursor-pointer border-b-[1px] border-gray-200 pt-4 pb-2 pr-4 text-start">
                      <p className="text-sm font-bold text-gray-600 dark:text-white">Phone</p>
                    </th>
                    <th className="cursor-pointer border-b-[1px] border-gray-200 pt-4 pb-2 pr-4 text-start">
                      <p className="text-sm font-bold text-gray-600 dark:text-white">Role</p>
                    </th>
                    <th className="cursor-pointer border-b-[1px] border-gray-200 pt-4 pb-2 pr-4 text-start">
                      <p className="text-sm font-bold text-gray-600 dark:text-white">Unit</p>
                    </th>
                    <th className="cursor-pointer border-b-[1px] border-gray-200 pt-4 pb-2 pr-4 text-start">
                      <p className="text-sm font-bold text-gray-600 dark:text-white">Created At</p>
                    </th>
                    <th className="cursor-pointer border-b-[1px] border-gray-200 pt-4 pb-2 pr-4 text-start">
                      <p className="text-sm font-bold text-gray-600 dark:text-white">Actions</p>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((user) => (
                    <tr key={user.id}>
                      <td className="min-w-[150px] border-b py-4 pr-4">
                        <p className="text-sm font-medium text-navy-700 dark:text-white">
                          {user.first_name}
                        </p>
                      </td>
                      <td className="min-w-[150px] border-b py-4 pr-4">
                        <p className="text-sm font-medium text-navy-700 dark:text-white">
                          {user.last_name}
                        </p>
                      </td>
                      <td className="min-w-[120px] border-b py-4 pr-4">
                        <p className="text-sm font-medium text-navy-700 dark:text-white">
                          {user.username}
                        </p>
                      </td>
                      <td className="min-w-[200px] border-b py-4 pr-4">
                        <p className="text-sm font-medium text-navy-700 dark:text-white">
                          {user.email}
                        </p>
                      </td>
                      <td className="min-w-[150px] border-b py-4 pr-4">
                        <p className="text-sm font-medium text-navy-700 dark:text-white">
                          {user.phone || '-'}
                        </p>
                      </td>
                      <td className="min-w-[100px] border-b py-4 pr-4">
                        <span className={`rounded-full px-2 py-1 text-xs font-medium ${
                          user.role === 'admin' 
                            ? 'bg-brand-500 text-white' 
                            : 'bg-gray-200 text-gray-700'
                        }`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="min-w-[150px] border-b py-4 pr-4">
                        <p className="text-sm font-medium text-navy-700 dark:text-white">
                          {user.unit || '-'}
                        </p>
                      </td>
                      <td className="min-w-[150px] border-b py-4 pr-4">
                        <p className="text-sm font-medium text-navy-700 dark:text-white">
                          {new Date(user.created_at).toLocaleDateString()}
                        </p>
                      </td>
                      <td className="min-w-[150px] border-b py-4 pr-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleEdit(user)}
                            className="flex items-center justify-center rounded-lg bg-lightPrimary p-2 text-brand-500 transition duration-200 hover:bg-gray-100 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-800"
                          >
                            <MdEdit className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleDelete(user.id)}
                            className="flex items-center justify-center rounded-lg bg-lightPrimary p-2 text-red-500 transition duration-200 hover:bg-gray-100 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-800"
                          >
                            <MdDelete className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {filteredUsers.length === 0 && (
                <div className="mt-4 flex h-[200px] items-center justify-center rounded-lg border border-gray-200 bg-gray-50 dark:border-navy-700 dark:bg-navy-800">
                  <p className="text-center text-gray-500 dark:text-gray-400">
                    No users found matching your search
                  </p>
                </div>
              )}

              {/* Pagination */}
              <div className="mt-4 flex items-center justify-between px-4">
                <div className="text-sm text-gray-600 dark:text-white">
                  Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredUsers.length)} of {filteredUsers.length} entries
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`rounded-lg px-3 py-1 text-sm ${
                      currentPage === 1
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-brand-500 text-white hover:bg-brand-600'
                    }`}
                  >
                    Previous
                  </button>
                  {[...Array(totalPages)].map((_, index) => (
                    <button
                      key={index + 1}
                      onClick={() => handlePageChange(index + 1)}
                      className={`rounded-lg px-3 py-1 text-sm ${
                        currentPage === index + 1
                          ? 'bg-brand-500 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-600'
                      }`}
                    >
                      {index + 1}
                    </button>
                  ))}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`rounded-lg px-3 py-1 text-sm ${
                      currentPage === totalPages
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-brand-500 text-white hover:bg-brand-600'
                    }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </Card>

      {selectedUser && (
        <EditUserModal
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          onUpdate={handleUpdate}
        />
      )}
    </div>
  );
};

export default UserList; 